<script context="module">
  export function focusNext(form) {
    const nodes = form.querySelectorAll("*");
    const tabbable = Array.from(nodes)
      .filter((n) => n.tabIndex >= 0 && !n.classList.contains("day--label"))
      .sort((a, b) => a.tabIndex - b.tabIndex);

    let index = tabbable.indexOf(document.activeElement);
    index += 1;
    if (index < tabbable.length) {
      tabbable[index].focus();
    }
  }
</script>

<script>
  import { createEventDispatcher, onDestroy } from "svelte";
  import { HStack, VStack, Spacer } from "./layout";
  import { Step } from "./widget";

  const dispatch = createEventDispatcher();
  const NOOP = () => {};

  export let form = undefined;
  export let step = 1;
  export let steps;
  export let showActions = true;

  export let submit = async () => {
    close();
  };

  export let validate = () => {};

  export let minHeight = "inherit";
  export let minWidth = "inherit";
  export let padding = true;
  export let closeOnEsc = true;
  export let allowBackButton = true;
  export let backButton = "Retour";
  export let cancelButton = "Annuler";
  export let continueButton = "Continuer";
  export let validateButton = "Valider";
  export let finalButton = "OK";

  let background;
  export let showInfo = false;
  export let infoMessage = null;
  export let showError = false;
  export let errorMessage = null;
  export let showWarning = false;
  export let warningMessage = null;
  export let translateError = (e) => {
    return e;
  };

  const reset = () => {
    showError = false;
    errorMessage = null;
    showInfo = false;
    infoMessage = null;
    showWarning = false;
    warningMessage = null;
  };

  $: currentStep = steps.find((s) => s.id == step);

  //$: console.log(step, currentStep);

  const close = async () => {
    dispatch("close");
  };

  const handle_back = async () => {
    reset();
    let step_back = currentStep.prev;
    if (step_back) {
      step = step_back;
      currentStep.done = false;
    }
    return false;
  };
  const handle_submit = async () => {
    reset();
    try {
      let r = await submit();
      if (r !== false) {
        currentStep.done = true;
        let step_next = currentStep.next;
        if (step_next) {
          step = step_next;
        }
      }
    } catch (e) {
      if (e.name == "Warning") {
        showWarning = true;
        warningMessage = e.message;
      } else if (e.name == "Info") {
        showInfo = true;
        infoMessage = e.message;
      } else {
        showError = true;
        errorMessage = translateError(e);
      }
    }
  };

  const handle_keydown = (e) => {
    if (e.key === "Escape" && closeOnEsc) {
      close();
      return;
    }

    if (e.key === "Tab") {
      const nodes = form.querySelectorAll("*");
      let tabbable = Array.from(nodes)
        .filter((n) => n.tabIndex >= 0 && !n.classList.contains("day--label"))
        .sort((a, b) => a.tabIndex - b.tabIndex);

      // trap focus
      let index = tabbable.indexOf(document.activeElement);
      if (index === -1 && e.shiftKey) index = 0;

      index += tabbable.length + (e.shiftKey ? -1 : 1);
      index %= tabbable.length;

      tabbable[index].focus();
      e.preventDefault();
    }
  };

  const previously_focused = typeof document !== "undefined" && document.activeElement;

  if (previously_focused) {
    onDestroy(() => {
      previously_focused.focus();
    });
  }
</script>

<svelte:window on:keydown={handle_keydown} />
<div class="modal-background" bind:this={background}>
  <div
    class="modal"
    class:padding
    style="min-height:{minHeight}; min-width:{minWidth}"
    role="dialog"
    aria-modal="true"
    on:click={(e) => e.stopPropagation()}>
    <VStack>
      <slot name="header">
        <header>
          <HStack justify="stretch" alignment="flex-start">
            <Spacer />
            {#each steps as s}
              <Step step={s} current={step} maxStep={steps.length} />
            {/each}
            <Spacer />
          </HStack>
        </header>
      </slot>
      {#if showError}
        <div class="error">{errorMessage}</div>
      {/if}
      {#if showWarning}
        <div class="warning">{warningMessage}</div>
      {/if}
      {#if showInfo}
        <div class="info">{infoMessage}</div>
      {/if}
      <form
        bind:this={form}
        class:error={showError}
        on:submit|preventDefault={handle_submit}
        on:invalid={validate}
        on:changed={validate}
        on:input={validate}>
        <slot />

        <slot name="actions">
          <div class="actions" class:hide={!showActions}>
            {#if allowBackButton && currentStep.back}
              <button
                formnovalidate
                type="button"
                class="secondary"
                on:click|preventDefault={handle_back}>
                {backButton}
              </button>
            {/if}
            <Spacer />
            {#if currentStep.cancel}
              <button class="cancel" type="button" on:click={close} tabIndex={99}>
                {cancelButton}
              </button>
            {/if}
            {#if currentStep.submit == "continue"}
              <button class="primary" type="submit">{continueButton}</button>
            {:else if currentStep.submit == "validate"}
              <button class="primary" type="submit">{validateButton}</button>
            {:else}
              <button class="primary" type="submit">{finalButton}</button>
            {/if}
          </div>
        </slot>
      </form>
    </VStack>
  </div>
</div>

<style>
  .modal-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    z-index: 99;
  }

  .modal {
    position: absolute;
    left: 50%;
    top: 50%;
    max-width: 32em;
    transform: translate(-50%, -50%);
    padding: 0;
    border-radius: 0.2em;
    background: white;
    z-index: 100;
  }

  .modal.padding {
    padding: 1em;
  }

  header {
    background-color: var(--primary-x-dark-gray-color);
    color: white;
    padding: 1em 0 0.5em 0;
    user-select: none;
  }

  form {
    padding: 1em;
  }
  .actions {
    display: flex;
  }
  .actions.hide {
    visibility: hidden;
  }

  button.primary {
    min-width: 100px;
  }

  button.cancel {
    margin-right: 1em;
    border-color: var(--primary-color);
  }

  button.secondary {
    background-color: white;
    color: var(--primary-dark-color);
    border-color: var(--primary-color);
  }

  div.error {
    color: var(--error-color);
    background-color: #fff;
    display: block;
    font-size: 12px;
    margin: auto;
    padding: 1em;
    margin-bottom: -2em;
  }

  div.warning {
    color: var(--secondary-light-color);
    background-color: #fff;
    display: block;
    font-size: 12px;
    margin: auto;
    padding: 1em;
    margin-bottom: -2em;
  }

  div.info {
    color: #61dd3b;
    background-color: #fff;
    display: block;
    font-size: 12px;
    margin: auto;
    padding: 1em;
    margin-bottom: -2em;
  }

  @media screen and (max-width: 600px) {
    .modal {
      width: 100%;
    }
    form {
      padding: 0.75em;
    }
  }
</style>
